import { rufe } from './client';
import type { Kategorie, Textbaustein } from './support';

/**
 * DIE PFLEGE DER TEXTBAUSTEINE (SUP-20; Auftrag CRM-178).
 *
 * Lesen mit `support/sehen`, Anlegen, Ändern und Entfernen mit
 * `support/vorlagen`. Die Auswahl in der Antwort (nur aktive, passend zu
 * Postfach und Kategorie) liegt in `support.ts` (`bausteinauswahl`).
 */

export type { Textbaustein } from './support';

/** SUP-20: die Platzhalter, die ein Baustein tragen kann — wie `support/domaene/textbaustein.ts`. */
export const PLATZHALTER: readonly { readonly name: string; readonly bedeutung: string }[] = [
  { name: 'kunde.name', bedeutung: 'Name des Kunden — Person mit Titel, sonst die Firmierung' },
  { name: 'kunde.anrede', bedeutung: '„Sehr geehrte Frau …", „Sehr geehrter Herr …" oder „Guten Tag", ohne Komma' },
  { name: 'kunde.nummer', bedeutung: 'Kundennummer' },
  { name: 'vorgang.nummer', bedeutung: 'Nummer des Vorgangs' },
  { name: 'buchung.nummer', bedeutung: 'Nummer der zugeordneten Buchung' },
  { name: 'termin.datum', bedeutung: 'Datum des Termins' },
  { name: 'termin.uhrzeit', bedeutung: 'Beginn des Termins' },
  { name: 'termin.produkt', bedeutung: 'Produkt des Termins' },
  { name: 'termin.standort', bedeutung: 'Standort des Termins' },
  { name: 'rechnung.nummer', bedeutung: 'Nummer der zugeordneten Rechnung' },
  { name: 'rechnung.betrag', bedeutung: 'Bruttobetrag der Rechnung' },
  { name: 'rechnung.faellig', bedeutung: 'Fälligkeit der Rechnung' },
  { name: 'bearbeiter.name', bedeutung: 'Name dessen, der antwortet' },
  { name: 'postfach.name', bedeutung: 'Bezeichnung des Postfachs' },
];

export type Bausteinfilter = {
  /** Kennung des Postfachs; null: alle. */
  readonly postfach: string | null;
  readonly kategorie: Kategorie | null;
  /** null: aktive und abgeschaltete. */
  readonly aktiv: boolean | null;
};

export type Bausteineingabe = {
  /** Kennung des Postfachs; null: für alle Postfächer. */
  readonly postfach: string | null;
  /** null: für alle Kategorien. */
  readonly kategorie: Kategorie | null;
  readonly titel: string;
  readonly betreff: string | null;
  readonly text: string;
  readonly aktiv: boolean;
};

/** SUP-20: die Bausteine, gefiltert nach Postfach, Kategorie und aktiv. */
export async function bausteine(f: Bausteinfilter, signal?: AbortSignal): Promise<readonly Textbaustein[]> {
  const abfrage = new URLSearchParams();
  if (f.postfach !== null) abfrage.set('postfach', f.postfach);
  if (f.kategorie !== null) abfrage.set('kategorie', f.kategorie);
  if (f.aktiv !== null) abfrage.set('aktiv', String(f.aktiv));
  const text = abfrage.toString();
  const antwort = await rufe<readonly Textbaustein[] | { readonly bausteine: readonly Textbaustein[] }>(
    `/support/textbausteine${text === '' ? '' : `?${text}`}`,
    signal === undefined ? {} : { signal },
  );
  // Der Server liefert die Liste nackt oder in einer Hülle — beides ist dieselbe Liste.
  return Array.isArray(antwort) ? antwort : (antwort as { readonly bausteine: readonly Textbaustein[] }).bausteine;
}

export async function bausteinAnlegen(eingabe: Bausteineingabe): Promise<Textbaustein> {
  return rufe('/support/textbausteine', { verfahren: 'POST', rumpf: eingabe });
}

export async function bausteinAendern(id: string, eingabe: Bausteineingabe): Promise<Textbaustein> {
  return rufe(`/support/textbausteine/${encodeURIComponent(id)}`, { verfahren: 'PUT', rumpf: eingabe });
}

/** Unumkehrbar — die Maske fragt vorher nach (Tabelle 29). */
export async function bausteinEntfernen(id: string): Promise<void> {
  await rufe(`/support/textbausteine/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}
